/**
 * Pure helpers for the Prediction screen — grouping, filtering, colours, overlay
 * geometry.
 *
 * Everything here is a function of its arguments. The hook owns requests and
 * the store owns state; this file exists so the arithmetic that decides what a
 * user actually sees can be tested without a browser, a server, or a trained
 * model.
 */

/**
 * The conf the SERVER is asked for, regardless of where the slider sits.
 *
 * The slider then filters client-side, so dragging it redraws instantly instead
 * of costing a forward pass per nudge. It is a floor rather than 0 so a
 * detector on a busy image does not return hundreds of boxes nobody would ever
 * lower the slider far enough to want.
 *
 * The rule this must keep: what is DISPLAYED is always the filtered set. Smart
 * mode shipped the other way round and was reported on 2026-09-13 — a raw list
 * kept for the slider's benefit, and the unfiltered list applied — which put 59
 * boxes on a photo of a bus, 34 of them below the threshold the user had set.
 */
export const REQUEST_CONF_FLOOR = 0.05;

export const DEFAULT_CONF = 0.25;

/** Five chart tokens, cycled. Read through `getToken` so both themes stay right. */
export const CLASS_COLOR_TOKENS = ['--chart-1', '--chart-2', '--chart-3', '--chart-4', '--chart-5'];

/**
 * A stable colour token for a class.
 *
 * Keyed on the class's INDEX IN THE TRAINED LIST, not on its position in this
 * image's results — otherwise the same class is drawn a different colour in
 * every photo, and a user comparing two images re-reads the legend each time.
 */
export const colorTokenForClass = (classId) => {
  const index = Number(classId);
  if (!Number.isFinite(index) || index < 0) return CLASS_COLOR_TOKENS[0];
  return CLASS_COLOR_TOKENS[Math.floor(index) % CLASS_COLOR_TOKENS.length];
};

/** `"Helmet detector · v2"` — what identifies a version in one line. */
export const versionLabel = (entry) => {
  if (!entry) return '';
  const version = entry.version === undefined || entry.version === null ? '' : String(entry.version);
  return version ? `${entry.model_name || 'Model'} · v${version}` : entry.model_name || 'Model';
};

/**
 * The flat catalogue, grouped project → model → versions for display.
 *
 * The API answers flat on purpose (searching across everything is a `filter`,
 * not a traversal); this is the display shape, rebuilt from whatever subset
 * survived the search box.
 */
export const groupByProject = (entries) => {
  const projects = new Map();
  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    const projectKey = entry.project_id || '_';
    if (!projects.has(projectKey)) {
      projects.set(projectKey, {
        project_id: entry.project_id,
        project_name: entry.project_name || 'Untitled project',
        models: new Map(),
      });
    }
    const project = projects.get(projectKey);
    const modelKey = entry.model_id || '_';
    if (!project.models.has(modelKey)) {
      project.models.set(modelKey, {
        model_id: entry.model_id,
        model_name: entry.model_name || 'Untitled model',
        model_type: entry.model_type,
        versions: [],
      });
    }
    project.models.get(modelKey).versions.push(entry);
  });

  return Array.from(projects.values()).map((project) => ({
    ...project,
    models: Array.from(project.models.values()),
  }));
};

/**
 * Search across project name, model name, version and CLASS names.
 *
 * Class names are in the haystack because that is how people look for a model —
 * "the one that finds helmets" — and they are the only field that says what a
 * model does rather than what somebody called it.
 */
export const filterEntries = (entries, query) => {
  const needle = String(query || '').trim().toLowerCase();
  const list = Array.isArray(entries) ? entries : [];
  if (!needle) return list;
  return list.filter((entry) => {
    const haystack = [
      entry.project_name,
      entry.model_name,
      entry.model_type,
      entry.version !== undefined && entry.version !== null ? `v${entry.version}` : '',
      ...(Array.isArray(entry.classes) ? entry.classes : []),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(needle);
  });
};

/**
 * The detections at or above the slider, best first.
 *
 * A detection with a null confidence is KEPT: the model produced it and we
 * were simply not told how sure it was. Dropping it would make a box vanish for
 * a reason no part of the screen could explain.
 */
export const visibleDetections = (detections, conf) => {
  const threshold = Number.isFinite(Number(conf)) ? Number(conf) : 0;
  return (Array.isArray(detections) ? detections : [])
    .filter((d) => d && (d.confidence === null || d.confidence === undefined || d.confidence >= threshold))
    .slice()
    .sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0));
};

/** `[{ class_id, class_name, count, best }]`, most-found first. */
export const summariseByClass = (detections) => {
  const buckets = new Map();
  (Array.isArray(detections) ? detections : []).forEach((detection) => {
    const key = `${detection.class_id}`;
    if (!buckets.has(key)) {
      buckets.set(key, { class_id: detection.class_id, class_name: detection.class_name, count: 0, best: 0 });
    }
    const bucket = buckets.get(key);
    bucket.count += 1;
    bucket.best = Math.max(bucket.best, detection.confidence ?? 0);
  });
  return Array.from(buckets.values()).sort((a, b) => b.count - a.count || b.best - a.best);
};

/**
 * The `viewBox` for the SVG that draws boxes over the image.
 *
 * Boxes arrive in PIXELS of the original image, so a viewBox of the original
 * size makes the browser do the scaling — the overlay then tracks the image
 * through every resize and column width with no JS and no measurement. Returns
 * null when the size is unknown, which is the signal not to draw an overlay at
 * all rather than to draw one at the wrong scale.
 */
export const overlayViewBox = (image) => {
  const width = Number(image?.width);
  const height = Number(image?.height);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return null;
  return { width, height, viewBox: `0 0 ${width} ${height}` };
};

/**
 * Stroke width in IMAGE pixels that renders as a constant ~2 display pixels.
 *
 * The SVG is scaled by the browser, so a fixed `strokeWidth` is multiplied by
 * that scale: a 4000px-wide photo shown in a 600px column would draw a hairline
 * and a 200px thumbnail a slab. Same arithmetic as the labeling canvas dividing
 * by the zoom — see `.claude/memory/labelflow-labeling-canvas.md`.
 */
export const overlayStrokeWidth = (image, renderedWidth) => {
  const natural = Number(image?.width);
  const rendered = Number(renderedWidth);
  if (!Number.isFinite(natural) || natural <= 0) return 2;
  if (!Number.isFinite(rendered) || rendered <= 0) return Math.max(natural / 400, 1);
  return Math.max((2 * natural) / rendered, 0.5);
};

/** `[[x,y],...]` → the `points` attribute of an SVG polygon. */
export const polygonPoints = (polygon) =>
  (Array.isArray(polygon) ? polygon : [])
    .filter((point) => Array.isArray(point) && point.length >= 2)
    .map(([x, y]) => `${x},${y}`)
    .join(' ');

/** `0.9123` → `"91.2%"`. An unknown confidence reads as `-`, never as 0%. */
export const formatConfidence = (value) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '-';
  return `${(Number(value) * 100).toFixed(1)}%`;
};

/**
 * `1234.5` → `"1.2s"`, `84.2` → `"84ms"`. An unknown duration reads `-`.
 *
 * The null check is explicit and comes FIRST because `Number(null)` is `0`,
 * which is finite and non-negative — so "we were never told" would render as
 * "0ms", a claim about speed nobody made. Same trap as `Number(null)` being a
 * valid cluster id in `autoGroup`, which quietly dumped every unassignable
 * image into the first class.
 */
export const formatDuration = (ms) => {
  if (ms === null || ms === undefined || ms === '') return '-';
  const value = Number(ms);
  if (!Number.isFinite(value) || value < 0) return '-';
  return value >= 1000 ? `${(value / 1000).toFixed(1)}s` : `${Math.round(value)}ms`;
};

/**
 * The i18n key suffix for a model type.
 *
 * `model_type` is a server string, so `` t(`prediction.type.${raw}`) `` would
 * render the literal key `prediction.type.pose` in a badge the day anything
 * widens that set — a missing key does not throw, it prints itself at the user.
 * Same shape as `WorkspaceContextPanel.normaliseStatus`: map to a known key, or
 * fall back to one.
 */
export const TYPE_KEYS = ['classification', 'detection', 'segmentation'];

export const typeKey = (modelType) =>
  TYPE_KEYS.includes(String(modelType || '').toLowerCase()) ? String(modelType).toLowerCase() : 'unknown';
